import {
  type HealthCheckConfig,
  isRestHealthCheck,
} from '@revelio/data-access';

export type SnippetFormat = 'curl' | 'python' | 'node' | 'json';

export const SNIPPET_FORMATS: ReadonlyArray<{
  id: SnippetFormat;
  label: string;
}> = [
  { id: 'curl', label: 'curl' },
  { id: 'python', label: 'Python (requests)' },
  { id: 'node', label: 'Node fetch' },
  { id: 'json', label: 'Raw JSON body' },
];

/** The env var a snippet reads the bearer token from — snippets never carry a real token. */
const AUTH_TOKEN_ENV = 'REVELIO_TOKEN';

/** The env var holding the reader's OWN session cookie, a different credential
    from the runner's admin token above — hence its own name. */
const SESSION_COOKIE_ENV = 'REVELIO_SESSION';

/** A header value is literal text or a reference to an environment variable. */
type HeaderValue =
  | { kind: 'literal'; text: string }
  | { kind: 'env'; name: string; prefix?: string };

export interface RequestSpec {
  method: 'GET' | 'POST';
  url: string;
  headers: Array<{ name: string; value: HeaderValue }>;
  /** Body text as sent; JSON bodies arrive pretty-printed. */
  body?: string;
}

const JSON_CONTENT_TYPE = {
  name: 'Content-Type',
  value: { kind: 'literal', text: 'application/json' },
} as const;

const BEARER_PLACEHOLDER = {
  name: 'Authorization',
  value: { kind: 'env', name: AUTH_TOKEN_ENV, prefix: 'Bearer ' },
} as const;

/** The runner sends the admin session as both a Bearer header and this cookie — see health-check.service.ts's REST fetch. */
const KRATOS_COOKIE_PLACEHOLDER = {
  name: 'Cookie',
  value: { kind: 'env', name: AUTH_TOKEN_ENV, prefix: 'ory_kratos_session=' },
} as const;

/**
 * How the calling surface authenticates, which is what a copied snippet has to
 * reproduce to be runnable. The health-check runner holds admin credentials and
 * sends a bearer token; the dashboard's own urql client sends no Authorization
 * header at all and rides the session cookie (`credentials: 'include'`), so a
 * bearer snippet copied from a page would 401 for every non-admin reader.
 */
type GraphqlAuth = 'bearer' | 'session-cookie';

const SESSION_COOKIE_PLACEHOLDER = {
  name: 'Cookie',
  value: {
    kind: 'env',
    name: SESSION_COOKIE_ENV,
    prefix: 'ory_kratos_session=',
  },
} as const;

export const graphqlRequestSpec = (input: {
  url: string;
  query: string;
  variables: Record<string, unknown>;
  auth?: GraphqlAuth;
}): RequestSpec => ({
  method: 'POST',
  url: input.url,
  headers: [
    JSON_CONTENT_TYPE,
    input.auth === 'session-cookie'
      ? SESSION_COOKIE_PLACEHOLDER
      : BEARER_PLACEHOLDER,
  ],
  body: JSON.stringify(
    { query: input.query, variables: input.variables },
    null,
    2
  ),
});

const ENV_PLACEHOLDER = /^\{([A-Z_][A-Z0-9_]*)\}$/;

/** Config header values may be `{ENV_VAR}` placeholders the runner resolves; keep them as references. */
const headerValueOf = (raw: string): HeaderValue => {
  const match = ENV_PLACEHOLDER.exec(raw);
  const name = match?.[1];
  return name ? { kind: 'env', name } : { kind: 'literal', text: raw };
};

export const restRequestSpec = (
  check: HealthCheckConfig,
  resolveUrl: (url: string) => string
): RequestSpec | null => {
  if (!isRestHealthCheck(check)) return null;
  const { request } = check;
  // Bespoke multi-step probes (e.g. 'resume-parser') don't send the plain
  // fetch below at all, so no snippet here would reproduce what the runner
  // actually does — hide Copy-as rather than render a misleading GET.
  if (request.flow) return null;
  const body =
    request.bodyRaw ??
    (request.body ? JSON.stringify(request.body, null, 2) : undefined);
  const headers = Object.entries(request.headers ?? {}).map(
    ([name, value]) => ({ name, value: headerValueOf(value) })
  );
  const hasContentType = headers.some(
    (header) => header.name.toLowerCase() === 'content-type'
  );
  if (body !== undefined && request.bodyRaw === undefined && !hasContentType) {
    headers.unshift(JSON_CONTENT_TYPE);
  }
  // The runner sends the admin session as both headers (see
  // health-check.service.ts) — a cookie-reading upstream would 401 a
  // Bearer-only copied request even though the runner itself succeeds.
  if (request.useAdminAuth) {
    headers.push(BEARER_PLACEHOLDER, KRATOS_COOKIE_PLACEHOLDER);
  }
  return {
    method: body === undefined ? 'GET' : 'POST',
    url: resolveUrl(request.url),
    headers,
    body,
  };
};

export const availableFormats = (spec: RequestSpec): SnippetFormat[] =>
  SNIPPET_FORMATS.map((format) => format.id).filter(
    (id) => id !== 'json' || spec.body !== undefined
  );

// ---------- renderers ----------

type Header = RequestSpec['headers'][number];

const shellSingleQuote = (text: string): string =>
  `'${text.replace(/'/g, `'"'"'`)}'`;

const curlHeader = ({ name, value }: Header): string =>
  value.kind === 'env'
    ? `-H "${name}: ${value.prefix ?? ''}$${value.name}"`
    : `-H ${shellSingleQuote(`${name}: ${value.text}`)}`;

const renderCurl = (spec: RequestSpec): string =>
  [
    spec.method === 'GET'
      ? `curl ${shellSingleQuote(spec.url)}`
      : `curl -X POST ${shellSingleQuote(spec.url)}`,
    ...spec.headers.map(curlHeader),
    ...(spec.body === undefined
      ? []
      : [`--data-raw ${shellSingleQuote(spec.body)}`]),
  ].join(' \\\n  ');

const pythonHeader = ({ name, value }: Header): string =>
  value.kind === 'env'
    ? `    ${JSON.stringify(name)}: f"${value.prefix ?? ''}{os.environ['${value.name}']}",`
    : `    ${JSON.stringify(name)}: ${JSON.stringify(value.text)},`;

/**
 * Escapes text for a non-raw Python triple-quoted string, so the closing
 * `"""` this renders next to the result is always unambiguous:
 *
 * 1. Backslashes first (`\` -> `\\`), so a JSON body's own `\n`/`\"` escapes
 *    survive verbatim once Python un-escapes them.
 * 2. Any run of quotes at the very end of the text, whatever its length
 *    (`"+$`) — escaping every quote in it. This must run before step 3:
 *    once a trailing run is escaped, its quotes are each preceded by a
 *    backslash, so nothing is left there for step 3 to (mis)match. Doing
 *    this step second instead would re-match the run's own last escaped
 *    quote as a fresh one-quote "trailing run" and escape it again,
 *    leaving a bare unescaped quote right where the closing `"""` gets
 *    appended — the same class of bug this function exists to prevent.
 * 3. Any remaining run of three or more quotes anywhere else in the text
 *    (`"{3,}`) — a run of 1 or 2 is safe as-is inside a Python
 *    triple-quoted string, only 3+ can be misread as (part of) the
 *    delimiter.
 *
 * Unlike a raw string, this is safe for arbitrary `bodyRaw` content (e.g.
 * CSV) as well as JSON — a raw string cannot escape a trailing quote or
 * backslash, both of which non-JSON payloads can end with.
 */
const pythonTripleQuoted = (text: string): string =>
  text
    .replace(/\\/g, '\\\\')
    .replace(/"+$/, (run) => '\\"'.repeat(run.length))
    .replace(/"{3,}/g, (run) => '\\"'.repeat(run.length));

const renderPython = (spec: RequestSpec): string =>
  [
    'import os',
    '',
    'import requests',
    '',
    `url = ${JSON.stringify(spec.url)}`,
    'headers = {',
    ...spec.headers.map(pythonHeader),
    '}',
    ...(spec.body === undefined
      ? []
      : [`body = """${pythonTripleQuoted(spec.body)}"""`]),
    '',
    spec.body === undefined
      ? 'response = requests.get(url, headers=headers, timeout=60)'
      : 'response = requests.post(url, data=body.encode("utf-8"), headers=headers, timeout=60)',
    'response.raise_for_status()',
    'print(response.text)',
    '',
  ].join('\n');

const jsTemplateLiteral = (text: string): string =>
  `\`${text.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${')}\``;

const nodeHeader = ({ name, value }: Header): string =>
  value.kind === 'env'
    ? `    ${JSON.stringify(name)}: \`${value.prefix ?? ''}\${process.env.${value.name}}\`,`
    : `    ${JSON.stringify(name)}: ${JSON.stringify(value.text)},`;

const renderNode = (spec: RequestSpec): string =>
  [
    `const response = await fetch(${JSON.stringify(spec.url)}, {`,
    `  method: ${JSON.stringify(spec.method)},`,
    '  headers: {',
    ...spec.headers.map(nodeHeader),
    '  },',
    ...(spec.body === undefined
      ? []
      : [`  body: ${jsTemplateLiteral(spec.body)},`]),
    '});',
    // biome-ignore lint/suspicious/noTemplateCurlyInString: printed Node.js snippet text, not real interpolation
    'if (!response.ok) throw new Error(`HTTP ${response.status}`);',
    'process.stdout.write(await response.text());',
    '',
  ].join('\n');

export const renderSnippet = (
  format: SnippetFormat,
  spec: RequestSpec
): string => {
  switch (format) {
    case 'curl':
      return renderCurl(spec);
    case 'python':
      return renderPython(spec);
    case 'node':
      return renderNode(spec);
    case 'json':
      return spec.body ?? '';
  }
};
